import React from 'react';
import {
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
  Fade,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  InputAdornment,
  Chip
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { extractApiErrorMessage, getAuthValueFromToken, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import { useStyles } from './styles';
import BusinessUnitLabel from '../BusinessUnitLabel';
import { LoaderSize } from '../../constants/constants';
import { Loader } from '../../elements';
import { ApiService } from '../../hooks';
import { useNavigate, useLocation } from 'react-router-dom';

const ChangeBusinessUnitPanel: React.FC<any> = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { state, updateToken, updateRefreshToken } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const apiService = new ApiService();

  const businessUnits = state.businessUnits ?? [];

  const [isBusy, setIsBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [searchFilter, setSearchFilter] = React.useState('');
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    if (businessUnits.length === 1) {
      return;
    }

    setOpen((prevOpen) => !prevOpen);
    if (!open) {
      setSearchFilter(''); // Clear search when opening panel
    }
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
    setSearchFilter('');
  };

  const filterBusinessUnits = (units: typeof businessUnits) => {
    if (!searchFilter.trim()) {
      return units;
    }

    const searchTerm = searchFilter.toLowerCase().trim();
    return units.filter((unit) => {
      const nameMatch = unit.name?.toLowerCase().includes(searchTerm);
      const numberMatch = unit.number?.toLowerCase().includes(searchTerm);
      const addressMatch = unit.address?.toLowerCase().includes(searchTerm);
      const typeMatch = unit.type?.toString().toLowerCase().includes(searchTerm);
      const roleMatch = unit.role?.toLowerCase().includes(searchTerm);

      return nameMatch || numberMatch || addressMatch || typeMatch || roleMatch;
    });
  };

  const renderBusinessUnitsList = () => {
    const filteredUnits = filterBusinessUnits(businessUnits);

    return (
      <Box>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          placeholder={t('f-login-search-business-unit')}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchFilter && (
              <InputAdornment position="end">
                <ClearIcon style={{ cursor: 'pointer' }} onClick={() => setSearchFilter('')} />
              </InputAdornment>
            )
          }}
          className={classes.searchField}
        />

        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={1}>
          <Typography variant="caption" color="textSecondary">
            {filteredUnits.length} of {businessUnits.length} units
          </Typography>
          {searchFilter && (
            <Chip
              size="small"
              label={t('f-login-clean-filter')}
              onDelete={() => setSearchFilter('')}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Box
          sx={{
            maxHeight: '300px',
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '8px'
          }}
        >
          <RadioGroup
            value={state.selectedBusinessUnitId}
            onChange={(e) => !isBusy && proceedChangeBusinessUnit(e.target.value)}
          >
            {filteredUnits
              .filter((unit) => unit.id)
              .sort((a, b) => {
                const typeCompare = String(a.type).localeCompare(String(b.type));
                if (typeCompare !== 0) return typeCompare;
                return String(a.name).localeCompare(String(b.name));
              })
              .map((unit) => {
                const isSelected = unit.id === state.selectedBusinessUnitId;
                return (
                  <Box
                    key={unit.id}
                    display="flex"
                    alignItems="center"
                    mb={1.5}
                    className={classes.businessUnitItem}
                    style={{ minWidth: 0 }}
                  >
                    <FormControlLabel
                      value={unit.id}
                      control={<Radio color="primary" disabled={isBusy} />}
                      label={
                        <BusinessUnitLabel unit={unit} cssClass={isSelected ? classes.selected : classes.notSelected} />
                      }
                      style={{ margin: 0, width: '100%', minWidth: 0 }}
                    />
                  </Box>
                );
              })}
            {filteredUnits.length === 0 && (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="textSecondary">
                  {t('f-login-no-results')}
                </Typography>
              </Box>
            )}
          </RadioGroup>
        </Box>
      </Box>
    );
  };

  const renderLoader = () => {
    return (
      <Box display="flex" justifyContent="center" p={2} pt={3} pb={3}>
        <Loader isLoading={isBusy} size={LoaderSize.MEDIUM} />
      </Box>
    );
  };

  const proceedChangeBusinessUnit = async (selectedId?: string) => {
    const unitIdToUse = selectedId ?? state.selectedBusinessUnitId;
    if (!unitIdToUse) {
      notifications.warning(t('f-warn-select-business-unit'));
      return;
    }

    setIsBusy(true);

    const currentPath = location.pathname;

    await apiService.api.auth
      .authLoginChangeBusinessUnit({ businessUnitId: unitIdToUse })
      .then(async (result) => {
        updateToken(result.data.token);
        updateRefreshToken(result.data.refreshToken);

        i18n.changeLanguage(getAuthValueFromToken(result.data.token).locale);

        const selectedUnit = result.data.businessUnits.find((unit) => unit.id === unitIdToUse);
        if (selectedUnit) {
          notifications.success(t('f-change-business-unit-success', { unit: selectedUnit.name }));
        }

        setIsBusy(false);
        setOpen(false);

        // Stay on same route only if switching within same unit category (Farm->Farm or Vet->Vet)
        const prevUnit = businessUnits.find((u) => u?.id === state.selectedBusinessUnitId);
        const nextUnit = result.data.businessUnits.find((u) => u?.id === unitIdToUse);
        const sameCategory = !!prevUnit && !!nextUnit && String(prevUnit.type) === String(nextUnit.type);

        const targetPath = sameCategory ? currentPath : '/dashboard';
        navigate(targetPath, { replace: true });
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setIsBusy(false);
      });
  };

  function getTooltipTitle(): NonNullable<React.ReactNode> {
    if (businessUnits.length > 1) {
      return t('f-change-business-unit-tooltip');
    }

    return '';
  }

  return (
    <Box>
      <Tooltip arrow title={getTooltipTitle()}>
        <IconButton ref={anchorRef} onClick={handleToggle} color="inherit">
          <Typography ref={anchorRef} variant="body1" color="inherit" noWrap>
            {state.farmNumber ?? state.vetNumber}
          </Typography>
        </IconButton>
      </Tooltip>

      {businessUnits.length > 1 && (
        <Popper
          key={'header-notifications-panel'}
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          className={classes.paper}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Fade
              {...TransitionProps}
              style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
            >
              <Paper elevation={5}>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box className={classes.container}>
                    <Typography variant="body2" gutterBottom>
                      {t('f-change-business-unit')}
                    </Typography>

                    {isBusy && renderLoader()}

                    {renderBusinessUnitsList()}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
      )}
    </Box>
  );
};

export default ChangeBusinessUnitPanel;
